import {
  Button,
  Group,
  Modal,
  NativeSelect,
  useModalsStack,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useAppSelector } from '@store';
import { CreateEditFields } from './CreateEditFields';

const defaultFieldTypes = [
  'checkboxGroup',
  'radioButtonGroup',
  'datepicker',
  'disclaimer',
  'divider',
  'email',
  'imagePicker',
  'infinite-list',
  'map',
  'multi-level-combobox',
  'picker',
  'products',
  'robeSizes',
  'signature',
  'tel',
  'text',
  'textarea',
  'title',
];

const getEmptyField = (type: string) => {
  const emptyField: any = {
    type,
    name: '',
    ...(type === 'title' || type === 'disclaimer' || type === 'divider'
      ? { title: '' }
      : { label: '' }),
    description: '',
    value: '',
    isRequired: false,
    isDisabled: false,
    isReadonly: false,
    items: [],
    ...(type === 'datepicker' ? { dateMinValue: 'today' } : {}),
    ...(type === 'disclaimer' ? { variant: '' } : {}),
    ...(type === 'robeSizes'
      ? {
          value: {
            xs: '',
            s: '',
            m: '',
            l: '',
            xl: '',
            classMasterXS: '',
            classMasterS: '',
            classMasterM: '',
            classMasterL: '',
            classMasterXL: '',
          },
        }
      : {}),
    ...(type === 'infinite-list'
      ? {
          inputs: [],
          diacritics: [],
          autoCorrect: [],
          value: [],
        }
      : {}),
    ...(type === 'text'
      ? {
          diacritics: [],
        }
      : {}),
    ...(type === 'textarea'
      ? {
          autoCorrect: [],
        }
      : {}),
    ...(type === 'multi-level-combobox'
      ? {
          customInput: {
            selectedValues: {},
            selectedValuesLabels: {},
            properties: {},
            values: [],
          },
          value: {},
        }
      : {}),
    ...(type === 'map' ? { value: null } : {}),
    ...(type === 'imagePicker'
      ? {
          availableImages: [],
          maxSelection: 1,
          imagesAutoImport: true,
          value: [],
        }
      : {}),
    ...(type === 'checkboxGroup'
      ? {
          items: [],
          value: '',
        }
      : {}),
  };

  return emptyField;
};

export const AddField = ({
  editField,
  onFieldUpdate,
  onAddField,
  onFieldAlreadyExists,
  onEditCancel,
}: {
  editField: any;
  onFieldUpdate: (field: any) => void;
  onAddField: (field: any) => void;
  onFieldAlreadyExists: (field: any) => boolean;
  onEditCancel?: () => void;
}) => {
  const intl = useIntl();
  const { settings } = useAppSelector((state) => state.settings);

  const stack = useModalsStack(['select-field-type', 'fill-field-details']);
  const [type, setType] = useState<string>(editField?.type || '');
  const [newField, setNewField] = useState<any>(editField || null);

  useEffect(() => {
    if (editField) {
      setType(editField.type);
      setNewField(editField);
      stack.open('fill-field-details');
    }
  }, [editField]);

  const handleAddField = () => {
    if (editField) {
      onFieldUpdate(newField);
    } else {
      if (onFieldAlreadyExists(newField)) {
        notifications.show({
          title: intl.formatMessage({ id: 'attention' }),
          message: `${intl.formatMessage({ id: 'fieldAlreadyExists' })}.`,
          color: 'red',
        });

        return;
      }

      onAddField(newField);
    }

    setType('');
    setNewField(null);
    stack.close('select-field-type');
    stack.close('fill-field-details');
  };

  return (
    <>
      <Modal.Stack>
        <Modal
          {...stack.register('select-field-type')}
          title={intl.formatMessage({ id: 'addField' })}
          size="lg"
          centered
          onClose={() => {
            setType('');
            setNewField(null);
            stack.close('select-field-type');
          }}
        >
          <NativeSelect
            label={intl.formatMessage({ id: 'type' })}
            name="type"
            value={type}
            onChange={(event) => {
              setType(event.currentTarget.value);
              setNewField(getEmptyField(event.currentTarget.value));
            }}
            data={[
              { value: '', label: '-' },
              ...defaultFieldTypes
                .map((_type: string) => ({
                  value: _type,
                  label: intl.formatMessage({ id: _type as any }),
                }))
                .sort((a, b) => a.label.localeCompare(b.label)),
            ]}
          />

          <Group mt="lg" justify="flex-end">
            <Button
              onClick={() => {
                setType('');
                setNewField(null);
                stack.close('select-field-type');
              }}
              variant="default"
            >
              {intl.formatMessage({ id: 'cancel' })}
            </Button>
            <Button
              disabled={!type}
              variant="outline"
              color="green"
              onClick={() => {
                // Check if imagePicker is selected and token is required
                if (type === 'imagePicker' && !settings?.digiStorageToken) {
                  notifications.show({
                    color: 'orange',
                    title: intl.formatMessage({ id: 'attention' }),
                    message: intl.formatMessage(
                      { id: 'digiStorageTokenRequiredForImagePicker' },
                      { fieldType: intl.formatMessage({ id: 'imagePicker' }) }
                    ),
                  });
                  return;
                }
                stack.open('fill-field-details');
              }}
            >
              {intl.formatMessage({ id: 'nextStep' })}
            </Button>
          </Group>
        </Modal>

        <Modal
          {...stack.register('fill-field-details')}
          title={
            type &&
            intl.formatMessage(
              { id: editField ? 'editTypeField' : 'addTypeField' },
              { type: intl.formatMessage({ id: type }) }
            )
          }
          size="lg"
          centered
          onClose={() => {
            setType('');
            setNewField(null);
            stack.close('select-field-type');
            stack.close('fill-field-details');
            onEditCancel?.();
          }}
        >
          <CreateEditFields
            field={newField}
            onFieldChange={(field: any) => {
              setNewField(field);
            }}
            // editField={null}
            // clearEditField={() => {
            //   setNewField(null);
            // }}
            // onUpdateFieldCallback={(field: any, newField: any) => {
            //   setNewField(newField);
            // }}
            // onAddFieldCallback={(field: any, callbackSuccess: () => void) => {
            //   setNewField(field);
            //   callbackSuccess();
            // }}
          />

          <Group mt="lg" justify="flex-end">
            <Button
              onClick={() => {
                setType('');
                setNewField(null);
                stack.close('select-field-type');
                stack.close('fill-field-details');
                onEditCancel?.();
              }}
              variant="default"
            >
              {intl.formatMessage({ id: 'cancel' })}
            </Button>
            <Button
              disabled={
                !type ||
                newField?.name === '' ||
                !newField ||
                ((type === 'title' ||
                  type === 'disclaimer' ||
                  type === 'divider') &&
                  !newField?.title) ||
                (type !== 'title' &&
                  type !== 'disclaimer' &&
                  type !== 'divider' &&
                  !newField?.label)
              }
              variant="outline"
              color="green"
              onClick={handleAddField}
            >
              {intl.formatMessage({
                id: editField ? 'updateField' : 'addField',
              })}
            </Button>
          </Group>
        </Modal>
      </Modal.Stack>

      <Button
        variant="outline"
        color="green"
        onClick={() => stack.open('select-field-type')}
      >
        {intl.formatMessage({ id: 'addField' })}
      </Button>
    </>
  );
};
